import { buildNewYorkTimestamp } from './usEquityMarketCalendar';

// ---------------------------------------------------------------------------
// Automatic Trade Replay session detection.
//
// All classification happens in the DST-aware IANA timezone America/New_York.
// The browser local timezone, UTC clock values, and the chart's display
// timezone (MARKET / LOCAL / UTC) must never influence classification.
// ---------------------------------------------------------------------------

export type TradeSessionStatus = 'PRE-MARKET' | 'REGULAR' | 'POST-MARKET' | 'MIXED SESSION' | 'FULL SESSION';

export type ExecutionSessionKind = 'PRE-MARKET' | 'REGULAR' | 'POST-MARKET';

export type TradeSessionRange = { startSec: number; endSec: number };

const NY_TZ = 'America/New_York';

const PRE_START_MIN = 4 * 60; // 04:00
const PRE_END_MIN = 9 * 60 + 30; // 09:30
const REG_END_MIN = 16 * 60; // 16:00
const POST_END_MIN = 20 * 60; // 20:00

/**
 * Minutes-of-day in America/New_York for an epoch-second timestamp.
 * Uses Intl with hourCycle h23 so midnight is 0 (never "24:00").
 */
export function getNyMinutesOfDay(epochSec: number): number | null {
  const n = Number(epochSec);
  if (!Number.isFinite(n) || n <= 0) return null;

  const dt = new Date(Math.floor(n) * 1000);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NY_TZ,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(dt);

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const hh = Number(map.hour);
  const mm = Number(map.minute);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

/**
 * Classify a single execution into a stock extended session.
 *
 * Boundary rules:
 *   - [04:00, 09:30)  -> PRE-MARKET
 *   - [09:30, 16:00)  -> REGULAR  (09:30 belongs to REGULAR)
 *   - [16:00, 20:00)  -> POST-MARKET (16:00 belongs to POST-MARKET)
 *   - otherwise       -> null (20:00 and later / before 04:00 are outside)
 */
export function classifyExecutionSession(epochSec: number): ExecutionSessionKind | null {
  const minutes = getNyMinutesOfDay(epochSec);
  if (minutes == null) return null;

  if (minutes >= PRE_START_MIN && minutes < PRE_END_MIN) return 'PRE-MARKET';
  if (minutes >= PRE_END_MIN && minutes < REG_END_MIN) return 'REGULAR';
  if (minutes >= REG_END_MIN && minutes < POST_END_MIN) return 'POST-MARKET';
  return null;
}

/**
 * Detect the trade session from ALL entry/exit execution timestamps (epoch
 * seconds UTC). Missing/empty execution data or executions outside the
 * supported 04:00–20:00 ET window fall back to FULL SESSION.
 */
export function detectTradeSession(executionTimesSec: Array<number | null | undefined>): TradeSessionStatus {
  const valid = (Array.isArray(executionTimesSec) ? executionTimesSec : [])
    .map((value) => Number(value))
    .filter((n) => Number.isFinite(n) && n > 0);

  if (valid.length === 0) return 'FULL SESSION';

  const seen = new Set<ExecutionSessionKind>();
  let sawOutside = false;

  for (const sec of valid) {
    const kind = classifyExecutionSession(Math.floor(sec));
    if (kind == null) {
      sawOutside = true;
    } else {
      seen.add(kind);
    }
  }

  if (seen.size === 0) return 'FULL SESSION'; // all executions outside 04:00–20:00
  if (seen.size > 1) return 'MIXED SESSION';
  if (sawOutside) return 'FULL SESSION'; // mixed in-session + outside -> safe fallback
  return [...seen][0];
}

/**
 * ET boundaries (epoch seconds UTC) for a detected session on the trade date.
 *
 *   PRE-MARKET    04:00–09:30
 *   REGULAR       09:30–16:00
 *   POST-MARKET   16:00–20:00
 *   MIXED/FULL    04:00–20:00
 */
export function getTradeSessionRangeSec(status: TradeSessionStatus, tradeDateYmd: string): TradeSessionRange {
  const msToSec = (hour: number, minute: number) => Math.floor(buildNewYorkTimestamp(tradeDateYmd, hour, minute) / 1000);

  switch (status) {
    case 'PRE-MARKET':
      return { startSec: msToSec(4, 0), endSec: msToSec(9, 30) };
    case 'REGULAR':
      return { startSec: msToSec(9, 30), endSec: msToSec(16, 0) };
    case 'POST-MARKET':
      return { startSec: msToSec(16, 0), endSec: msToSec(20, 0) };
    case 'MIXED SESSION':
    case 'FULL SESSION':
    default:
      return { startSec: msToSec(4, 0), endSec: msToSec(20, 0) };
  }
}

/**
 * New York trading date (YYYY-MM-DD) for an epoch-second timestamp.
 */
export function getNyDateKey(epochSec: number): string | null {
  const n = Number(epochSec);
  if (!Number.isFinite(n) || n <= 0) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(Math.floor(n) * 1000));
}

/**
 * Distinct New York dates spanned by the executions, sorted ascending.
 */
export function executionNyDateKeys(executionTimesSec: Array<number | null | undefined>): string[] {
  const seen = new Set<string>();
  for (const value of executionTimesSec || []) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) continue;
    const key = getNyDateKey(Math.floor(n));
    if (key) seen.add(key);
  }
  return [...seen].sort();
}
